import { Divider, Grid, Typography } from "@mui/material";
import theme from "Theme/AppTheme";
import AcxEmptyState from "components/UI/AcxEmptyStateIndicator/AcxEmptyState";
import AcxDataGrid from "components/UI/AcxDataGrid/AcxDataGrid";
import { observer } from "mobx-react";
import { useMemo, useRef } from "react";
import { defaultShadowsForSignals } from "../../SignalsUtils";
import TableCardStore from "./TableCardStore";
import { useCurrentDashboard } from "components/Signals/useCurrentDashboard";
import {
    AcxDeprecatedTag,
    useStatusTagVisible,
} from "components/UI/StatusTags";
import TableMenu from "./TableMenu";
import TableControls from "./TableControls";
import PointMenu from "../Drilldown/PointMenu";
import DrilldownDialog from "../Drilldown/DrilldownDialog";
import { ObjectFiltersChips } from "../ObjectFilters";
import AcxDialog from "components/UI/Dialog/AcxDialog";
import { JSONViewer } from "components/UI/JSONViewer";
import { resolveDataAvailability } from "components/Signals/DataAvailability/DataAvailability";
import DataAvailabilityInfo from "components/Signals/DataAvailability/DataAvailabilityInfo";
import { AuthStore } from "stores/AuthStore";
import { useStore } from "utils/useStore";

const TableCard = observer(({ store }: { store: TableCardStore }) => {
    const authStore = useStore(AuthStore);
    const cardRef = useRef<HTMLDivElement>(null);
    const currentDashboard = useCurrentDashboard();

    // Presentation mode is a clean read-only view, so the deprecation marker is suppressed there.
    // `.no-export` additionally strips it from the per-chart PNG export.
    // Called unconditionally: the tag may declare a permission, and hooks cannot be
    // called behind the Deprecated/presentation-mode checks below.
    const isStatusTagVisible = useStatusTagVisible(store.config.status);
    const deprecation =
        isStatusTagVisible &&
        store.config.status?.tag === "Deprecated" &&
        !currentDashboard?.isPresentationMode
            ? store.config.status
            : undefined;

    const validData = useMemo(
        () => (store.data?.length ?? 0) > 0,
        [store.data],
    );
    const dataGridHeight = useMemo(() => {
        const visibleRows =
            store.config.renderOptions.dataGridNumVisibleRows ?? 5;
        const footerHeight = store.dataGridStore?.hideFooterPagination ? 0 : 52;
        const headerHeight = 52;
        const rowHeight = 43;
        const removeHeight =
            Number.parseInt(store.dataGridStore?.removeHeight ?? "0", 10) || 0;

        return `${
            headerHeight + footerHeight + rowHeight * visibleRows + removeHeight
        }px`;
    }, [
        store.dataGridStore?.hideFooterPagination,
        store.dataGridStore?.removeHeight,
        store.config.renderOptions.dataGridNumVisibleRows,
    ]);

    const gridHasRows = (store.dataGridStore?.rows.length ?? 0) > 0;
    const gridIsLoading = store.dataGridStore?.isLoading;
    const showCardLoader =
        store.isWaitingOnRequiredOptions ||
        store.isWaitingOnSelectionDefaults ||
        store.isRefreshing;
    const chartErrorPresent = store.error;
    const showNoDataState = !showCardLoader && !chartErrorPresent && !validData;
    const dataAvailability = resolveDataAvailability({
        authStore,
        policy: store.config.dataAvailability,
        defaultEmptyStateVariant:
            store.config.renderOptions.emptyStateVariant,
        startDate: store.state?.startDate,
        endDate: store.state?.endDate,
        hasRenderableData: validData,
    });
    const showDataAvailabilityInfo =
        !!dataAvailability.infoKind &&
        !showCardLoader &&
        !chartErrorPresent &&
        !store.isPresentationMode &&
        !currentDashboard?.isExportCaptureInProgress;
    const showCenteredState =
        showCardLoader || !!chartErrorPresent || showNoDataState;
    const cardBodyMinHeight = dataGridHeight;

    return (
        <>
            <PointMenu
                store={store}
                open={store.pointMenuAnchor !== null}
                anchorPosition={store.pointMenuAnchor ?? undefined}
                onClose={() => store.closePointMenu()}
            />
            <DrilldownDialog
                baseObjectStore={store}
                drilldownStore={store.drilldownStore}
            />
            <AcxDialog
                isOpen={store.jsonCellViewer.isOpen}
                onClose={() => store.closeJsonCellViewer()}
                title={store.jsonCellViewer.title}
                maxWidth="md"
                fullWidth
                contentWidth="100%"
                hideCancelButton
                dialogContentChildren={
                    <JSONViewer
                        value={store.jsonCellViewer.value}
                        display="rich"
                        sx={{ maxHeight: "60vh", overflow: "auto", margin: 0 }}
                    />
                }
            />
            <Grid
                sx={{
                    borderRadius: 2,
                    width: "100%",
                    height: "100%",
                    boxShadow: defaultShadowsForSignals,
                    scrollMarginTop: "150px",
                    backgroundColor: theme.palette.white.main,
                }}
                id={store.id}
                ref={cardRef}
            >
                <Grid
                    container
                    item
                    xs={12}
                    justifyContent={"space-between"}
                    sx={{
                        paddingInline: theme.spacing(3),
                        paddingBlock: theme.spacing(2),
                    }}
                >
                    <Grid
                        container
                        item
                        xs={11}
                        direction={"column"}
                        justifyContent="center"
                    >
                        <Typography variant="h2">
                            <Grid
                                container
                                direction="row"
                                item
                                alignItems="center"
                                gap={1}
                            >
                                {store.title}
                                {deprecation && (
                                    <Grid item className="no-export">
                                        <AcxDeprecatedTag
                                            infoText={deprecation.infoText}
                                        />
                                    </Grid>
                                )}
                                {showDataAvailabilityInfo &&
                                    dataAvailability.infoKind && (
                                        <Grid item className="no-export">
                                            <DataAvailabilityInfo
                                                kind={
                                                    dataAvailability.infoKind
                                                }
                                            />
                                        </Grid>
                                    )}
                            </Grid>
                        </Typography>

                        <Typography sx={{ color: theme.palette.neutral[600] }}>
                            {store.subtitle}
                        </Typography>
                    </Grid>
                    {store.config.renderOptions.menu && (
                        <Grid item alignSelf="center">
                            <TableMenu
                                store={store}
                                chartContainerRef={cardRef}
                                isValidData={validData}
                            />
                        </Grid>
                    )}
                    <ObjectFiltersChips store={store} />
                </Grid>
                <Grid item xs={12}>
                    <Divider />
                </Grid>
                <Grid
                    container
                    sx={{
                        paddingInline: theme.spacing(3),
                        paddingBottom: theme.spacing(2),
                        paddingTop: theme.spacing(1),
                    }}
                >
                    <Grid item container xs={12} className="no-export">
                        <TableControls store={store} />
                    </Grid>

                    <Grid
                        item
                        xs={12}
                        sx={{
                            // The capture sizes the body to its rows; the on-screen minimum
                            // would leave blank space under them in the PNG.
                            minHeight: store.isPngCaptureInProgress
                                ? undefined
                                : cardBodyMinHeight,
                        }}
                    >
                        {showCenteredState && (
                            <Grid
                                container
                                sx={{
                                    minHeight: cardBodyMinHeight,
                                }}
                                alignItems="center"
                                justifyContent="center"
                            >
                                {showCardLoader && (
                                    <AcxEmptyState
                                        size="small"
                                        variant="loading"
                                    />
                                )}
                                {!showCardLoader && chartErrorPresent && (
                                    <AcxEmptyState
                                        size="small"
                                        variant="error"
                                    />
                                )}
                                {showNoDataState && (
                                    <AcxEmptyState
                                        size="small"
                                        variant={
                                            dataAvailability.emptyStateVariant
                                        }
                                    />
                                )}
                            </Grid>
                        )}

                        {!showCardLoader &&
                            !chartErrorPresent &&
                            store.dataGridStore &&
                            (gridHasRows || gridIsLoading) && (
                                // During PNG capture the grid grows to fit every row on
                                // the page so the export shows them all; see TableMenu.
                                <Grid
                                    container
                                    id={"dg-" + store.id}
                                    sx={{
                                        height: store.isPngCaptureInProgress
                                            ? "auto"
                                            : dataGridHeight,
                                    }}
                                >
                                    <AcxDataGrid
                                        dataGridStore={store.dataGridStore}
                                        exportCapture={
                                            store.isPngCaptureInProgress
                                        }
                                    />
                                </Grid>
                            )}
                    </Grid>
                </Grid>
            </Grid>
        </>
    );
});

export default TableCard;
